// NAV-LEKÉRÉS INDÍTÓ GOMB + állapot-jelzés a Kiadások fülön.
//
// MIÉRT VAN a napi cron mellett: a beszállító késve is beküldheti az adatot, és a számlázós kollégának
// néha MOST kell látnia a hónapot (hó-zárás, egyeztetés). A lekérés inkrementális és csak beszúr, tehát a
// kézi újrakérés SOHA nem duplikál — a `skipped` a már nálunk lévő bizonylatokat jelenti, nem hibát.
//
// AZ ÁLLAPOT KIMONDJA, AMI NEM LÁTSZIK: hogy be van-e kötve a technikai felhasználó, és hogy MIKOR JÁR LE
// az aláírókulcs. A lejáratot a NAV API nem adja meg (az üzemeltető írja a configba) — figyelmeztetés
// nélkül a csere elmulasztása néma import-leállás lenne: a lap nem hibázna, csak nem jönne új sor.

import { ArrowDownTrayIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type TFunction } from 'i18next';
import { toast } from 'sonner';

import { expenseKeys } from '@/features/expenses/api';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { api } from '@/lib/api';

/** Ennyi nappal a lejárat előtt figyelmeztet a felület (a cron naplója ugyanennyivel). */
const WARN_DAYS = 30;

/**
 * A NAV-LEKÉRÉS VEZÉRLŐJE — mutáció + a kapcsolódó státusz, EGY példányban.
 *
 * MIÉRT HOOK: a lekérést KÉT felület indítja — a deszktop sáv-gombja és a MOBIL lebegő FAB (ahol a sáv-gombok
 * el vannak rejtve). Ha mindkettő saját mutációt tartana, a „fut éppen" állapot szétcsúszna, és két kérés is
 * elmehetne. Így a lap egyszer hívja meg, és mindkét vezérlő ugyanazt az állapotot látja.
 */
export function useNavSync(t: TFunction): {
  run: () => void;
  pending: boolean;
  /** A NAV-integráció ki van-e kapcsolva ezen a környezeten (ilyenkor a vezérlő tiltott). */
  disabled: boolean;
  signKeyDaysLeft: number | null;
  signKeyExpiresAt: string | null;
} {
  const qc = useQueryClient();
  const statusKey = ['expenses', 'nav-status'] as const;
  const statusQ = api.expenses.navStatus.useQuery(statusKey, undefined, { queryKey: statusKey });
  const status = statusQ.data?.status === 200 ? statusQ.data.body : undefined;

  const sync = useMutation({
    mutationFn: () => api.expenses.navSync.mutation({ body: {} }),
    onSuccess: (res) => {
      if (res.status === 200) {
        const report = res.body;
        // A BUKOTT SOROK külön sorban: a `failed` ÚJRAPRÓBÁLHATÓ (fojtás, kiesés), a `reportLine`
        // számpárja mögött elveszne — pedig épp ez az, amiért vissza kell jönni.
        const reportLine = [
          t('expenses.nav.reportLine', { created: report.created, skipped: report.skipped }),
          // AUTOMATIKUS ELVETÉS: a teljes sztornó-párok. Csak ha történt — a napi „0 elvetve" zaj lenne, és
          // a felhasználónak tudnia KELL róla, mert sorok tűntek el a listájáról.
          report.dismissed > 0
            ? t('expenses.nav.reportDismissed', { count: report.dismissed })
            : null,
          report.failed > 0 ? t('expenses.nav.reportFailed', { count: report.failed }) : null,
        ]
          .filter((line): line is string => line !== null)
          .join(' · ');
        if (report.status === 'failed' || report.status === 'partial') {
          toast.error(t('expenses.nav.failed', { code: report.errorCode ?? '—' }), {
            description: reportLine,
          });
        } else if (report.created === 0) {
          // Nulla ÚJ sor: nem hiba (a hónap adatai már nálunk vannak) — de nem is siker-élmény, ezért info.
          toast.info(t('expenses.nav.nothingNew'), { description: reportLine });
        } else {
          toast.success(t('expenses.nav.done', { count: report.created }), {
            description: reportLine,
          });
        }
        void qc.invalidateQueries({ queryKey: expenseKeys.all });
        void qc.invalidateQueries({ queryKey: statusKey });
        return;
      }
      if (res.status === 503) {
        toast.error(t('expenses.nav.disabled'));
        return;
      }
      if (res.status === 403) {
        toast.error(t('toast:expenses.forbidden'));
        return;
      }
      toast.error(t('toast:expenses.error'));
    },
    onError: () => toast.error(t('toast:expenses.error')),
  });

  return {
    run: () => sync.mutate(),
    pending: sync.isPending,
    disabled: status?.configured === false,
    signKeyDaysLeft: status?.signKeyDaysLeft ?? null,
    signKeyExpiresAt: status?.signKeyExpiresAt ?? null,
  };
}

export function NavSyncButton({
  canManage,
  sync,
  t,
}: {
  canManage: boolean;
  /** A KÖZÖS vezérlő (`useNavSync`) — a lap adja, hogy a mobil-FAB ugyanazt az állapotot ossza. */
  sync: ReturnType<typeof useNavSync>;
  t: TFunction;
}): JSX.Element {
  const expiringSoon = sync.signKeyDaysLeft !== null && sync.signKeyDaysLeft <= WARN_DAYS;

  const button = (
    <Button
      variant="outline"
      className="shrink-0"
      disabled={!canManage || sync.disabled || sync.pending}
      onClick={sync.run}
    >
      <ArrowDownTrayIcon className="size-4" />
      <span className="hidden sm:inline">
        {sync.pending ? t('expenses.nav.running') : t('expenses.nav.action')}
      </span>
    </Button>
  );

  return (
    <div className="flex items-center gap-2">
      {expiringSoon && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-warning flex items-center gap-1 text-xs">
              <ExclamationTriangleIcon className="size-4" />
              <span className="hidden lg:inline">{t('expenses.nav.keyExpiringShort')}</span>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {sync.signKeyDaysLeft !== null && sync.signKeyDaysLeft < 0
              ? t('expenses.nav.keyExpired', { date: sync.signKeyExpiresAt ?? '' })
              : t('expenses.nav.keyExpiring', {
                  days: sync.signKeyDaysLeft ?? 0,
                  date: sync.signKeyExpiresAt ?? '',
                })}
          </TooltipContent>
        </Tooltip>
      )}
      {sync.disabled ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0}>{button}</span>
          </TooltipTrigger>
          <TooltipContent>{t('expenses.nav.disabled')}</TooltipContent>
        </Tooltip>
      ) : (
        button
      )}
    </div>
  );
}

/**
 * AZ UTOLSÓ NAV-BIZONYLAT DÁTUMA — a hónap-fejléc jelvény-sorába való, nem az eszköztárba.
 *
 * MIÉRT KÖLTÖZÖTT: mellékes információ (mikor jártunk utoljára a NAV-nál), az akció-sávban viszont a
 * gombokkal azonos súlyt kapott, és zajossá tette a sort. A helye az ÖSSZEGZŐ jobb kártyája, PASSZÍV
 * állapot-sorként (`ExpenseSummaryCards`) — ezért ad ez a hook nyers DÁTUMOT, nem kész jelvényt: a
 * megjelenítést a kártya dönti el, itt csak az adat-forrás van.
 *
 * Ugyanazt a lekérdezést olvassa, mint a gomb — a TanStack Query cache-éből, tehát nincs plusz kérés.
 */
export function useNavLastImportDate(): string | null {
  const statusKey = ['expenses', 'nav-status'] as const;
  const statusQ = api.expenses.navStatus.useQuery(statusKey, undefined, { queryKey: statusKey });
  const status = statusQ.data?.status === 200 ? statusQ.data.body : undefined;
  return status?.lastInsDate == null ? null : status.lastInsDate.slice(0, 10);
}
